<template>
	<div ref="zoneRef">
		<div v-if="scoringEnabled" class="points-statement-zone">
			<div class="points-statement-zone__row">
				<v-icon class="points-statement-zone__icon">trophy</v-icon>
				<span class="points-statement-zone__label">{{ t('PointsOnThisOffer') }}</span>

				<div v-if="historyLoaded" class="points-statement-zone__history">
					<template v-if="hasHistory">
						<span v-if="pointsSums.validated > 0" class="points-statement-zone__stat points-statement-zone__stat--validated">
							<v-icon>check_circle</v-icon>
							<strong>{{ formatPoints(pointsSums.validated) }} {{ t('pts') }}</strong>
							{{ t('Validated') }}
						</span>
						<span v-if="pointsSums.pending > 0" class="points-statement-zone__stat points-statement-zone__stat--pending">
							<v-icon>hourglass_empty</v-icon>
							<strong>{{ formatPoints(pointsSums.pending) }} {{ t('pts') }}</strong>
							{{ t('Pending') }}
						</span>
						<span v-if="pointsSums.denied > 0" class="points-statement-zone__stat points-statement-zone__stat--denied">
							<v-icon>cancel</v-icon>
							<strong>{{ formatPoints(pointsSums.denied) }} {{ t('pts') }}</strong>
							{{ t('Denied') }}
						</span>
					</template>
					<span v-else class="points-statement-zone__empty">
						<v-icon>info</v-icon>
						{{ t('NoPointsRequestYet') }}
					</span>
				</div>

				<v-spacer />

				<a v-if="historyLoaded && hasHistory" class="points-statement-zone__detail-link" @click.prevent="openHistory">{{ t('SeeDetail') }} ›</a>
				<PointsClaimButton v-if="points > 0" :campaign-id="campaignId" :offer-id="offerId" :appointment-uid="appointmentUid" :delivery-location-id="deliveryLocationId" :promo-name="promoName" :points="points" :overlay-target="overlayTarget" :campaign-offer-start-date="campaignOfferStartDate" :campaign-offer-end-date="campaignOfferEndDate" :readonly="readonly" />
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, computed, inject, onMounted, onBeforeUnmount } from 'vue';
import PointsClaimButton from './PointsClaimButton.vue';
import { calculatePoints, isScoringMatrixAvailable } from '@/services/pointsMatrixService';
import { getPointsHistory, matchesOfferContext, roundPoints, formatPoints, isScoringPeriodClosed } from '@/api/sales/points';
import { useTranslator } from '@/composables/useTranslator';

const t = useTranslator();
const zoneRef = ref(null);
const overlayTarget = inject('overlayTarget', null);

const campaignId = ref(null);
const offerId = ref(null);
const appointmentUid = ref(null);
const deliveryLocationId = ref(null);
const promoName = ref('');
const scoringEnabled = ref(false);
const readonly = ref(false);
const points = ref(0);
const campaignOfferStartDate = ref(null);
const campaignOfferEndDate = ref(null);

const historyLoaded = ref(false);
const offerHistory = ref([]);

let _mountTarget = null;

const pointsSums = computed(() => ({
	validated: roundPoints(offerHistory.value.filter((e) => e.status === 'VALIDATED' || e.status === 'AUTO').reduce((sum, e) => sum + roundPoints(e.pointsValidated ?? e.pointsClaimed ?? 0), 0)),
	pending: roundPoints(offerHistory.value.filter((e) => e.status === 'PENDING').reduce((sum, e) => sum + roundPoints(e.pointsClaimed ?? 0), 0)),
	denied: roundPoints(offerHistory.value.filter((e) => e.status === 'DENIED').reduce((sum, e) => sum + roundPoints(e.pointsClaimed ?? 0), 0)),
}));

const hasHistory = computed(() => offerHistory.value.length > 0);

function offerContext() {
	return {
		campaignId: campaignId.value,
		offerId: offerId.value,
		campaignOfferStartDate: campaignOfferStartDate.value,
		campaignOfferEndDate: campaignOfferEndDate.value,
		deliveryLocationId: deliveryLocationId.value,
	};
}

async function loadOfferHistory() {
	try {
		if (await isScoringPeriodClosed()) {
			scoringEnabled.value = false;
			offerHistory.value = [];
			return;
		}

		const history = await getPointsHistory();
		const context = offerContext();
		offerHistory.value = (history ?? []).filter((e) => matchesOfferContext(e, context));
	} catch {
		offerHistory.value = [];
	} finally {
		historyLoaded.value = true;
		if (_mountTarget) _mountTarget._pointsHasHistory = hasHistory.value;
	}
}

function openHistory() {
	window.dispatchEvent(new CustomEvent('open-vue-points-dialog', { detail: { ...offerContext(), deliveryLocationId: deliveryLocationId.value } }));
}

function handleContext(event) {
	const { campaignId: cId, offerId: oId, appointmentUid: aUid, deliveryLocationId: dlId, scoringEnabled: scoring, readonly: isReadonly, promoData, campaignOfferStartDate: startDate, campaignOfferEndDate: endDate } = event.detail;
	campaignId.value = cId ?? null;
	offerId.value = oId ?? null;
	appointmentUid.value = aUid ?? null;
	deliveryLocationId.value = dlId ?? null;
	promoName.value = promoData?.promoName ?? '';
	readonly.value = !!isReadonly;
	scoringEnabled.value = !!scoring && isScoringMatrixAvailable();
	points.value = scoringEnabled.value ? calculatePoints({ volumeUC: promoData?.volumeUC, meaCount: promoData?.meaCount, presentCount: promoData?.presentCount }) : 0;
	campaignOfferStartDate.value = startDate ?? null;
	campaignOfferEndDate.value = endDate ?? null;

	if (scoringEnabled.value) loadOfferHistory();
}

// A claim/update submitted from any zone (this offer, another offer, another visit) clears the
// shared points cache and fires this — re-run the fetch so this recap picks up the change instead
// of being stuck showing whatever it read at mount time.
function handlePointsUpdated() {
	if (scoringEnabled.value) loadOfferHistory();
}

onMounted(() => {
	_mountTarget = zoneRef.value?.parentElement;
	_mountTarget?.addEventListener('vue:points-context', handleContext);
	window.addEventListener('points-updated', handlePointsUpdated);

	if (_mountTarget?._pendingPointsContext) {
		handleContext({ detail: _mountTarget._pendingPointsContext });
	}
});

onBeforeUnmount(() => {
	_mountTarget?.removeEventListener('vue:points-context', handleContext);
	window.removeEventListener('points-updated', handlePointsUpdated);
	_mountTarget = null;
});
</script>
